import { useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGlobalHotkey } from "@/hooks/use-global-hotkey";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { getStatusConfig } from "@shared/status-config";
import { hasViewResetOwner } from "@/lib/esc-reset-coordination";

interface StatusData {
  id: number;
  availabilityStatus?: string;
}

export const ESC_STATUS_RESET_WINDOW_MS = 2000;

/**
 * Pure arming logic for the double-ESC status reset, extracted so it can be
 * unit-tested without a DOM: returns `"fire"` when this press completes a
 * double press within the window, else `"arm"`.
 */
export function decideEscPress(
  armedAt: number | null,
  now: number,
  windowMs = ESC_STATUS_RESET_WINDOW_MS,
): "arm" | "fire" {
  return armedAt !== null && now - armedAt <= windowMs ? "fire" : "arm";
}

/**
 * App-wide: while the current user's availability status is anything other
 * than "working" (AFK, Rendering, In a Meeting, …), pressing ESC twice
 * within two seconds switches it back to "working". Inactive (no listener
 * side effects, no toasts) while already working, so ESC keeps its normal
 * meaning everywhere else. `useGlobalHotkey` already bails on inputs,
 * contenteditable and open dialogs.
 */
export function useEscStatusReset(userId: number | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: statuses } = useQuery<StatusData[]>({
    queryKey: ["/api/users/availability-statuses"],
    enabled: !!userId,
  });
  const myStatus = statuses?.find((s) => s.id === userId)?.availabilityStatus || "working";
  const away = !!userId && myStatus !== "working";

  const armedAtRef = useRef<number | null>(null);
  const handler = useCallback((event: KeyboardEvent) => {
    if (event.repeat) return;
    const now = Date.now();
    if (decideEscPress(armedAtRef.current, now) === "fire") {
      armedAtRef.current = null;
      apiRequest("PATCH", "/api/user/availability-status", { status: "working" })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["/api/users/availability-statuses"] });
          if (!hasViewResetOwner()) {
            toast({ description: `Status switched back to ${getStatusConfig("working").label}.` });
          }
        })
        .catch(() => {
          toast({
            description: "Could not reset your status — please try via the user menu.",
            variant: "destructive",
          });
        });
      return;
    }
    armedAtRef.current = now;
    // On the dashboard the view-reset handler words its own hint to include
    // the status (only one toast can show at a time).
    if (hasViewResetOwner()) return;
    toast({
      description: `Press ESC again to switch from ${getStatusConfig(myStatus).label} back to ${getStatusConfig("working").label}.`,
      duration: ESC_STATUS_RESET_WINDOW_MS,
    });
  }, [myStatus, queryClient, toast]);

  useGlobalHotkey("Escape", handler, {
    enabled: away,
    description: "Switch status back to Working (press twice, only while away)",
    group: "Status",
  });
}
